import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/shared/Layout';
import { Alert } from '../components/shared/Alert';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { authApi } from '../services/api';
import { CheckCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const email = searchParams.get('email');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!email || !token) {
        setErrorMessage('Invalid verification link. Please check your email and try again.');
        setStatus('error');
        return;
      }

      try {
        await authApi.verifyEmail(email, token);
        setStatus('success');
      } catch (err: unknown) {
        const error = err as { response?: { data?: { message?: string } } };
        setErrorMessage(
          error.response?.data?.message ||
            'Failed to verify your email. The link may have expired. Please request a new verification email.'
        );
        setStatus('error');
      }
    };

    verifyEmail();
  }, [email, token]);

  return (
    <Layout>
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md w-full">
          {status === 'loading' && (
            <div className="text-center">
              <EnvelopeIcon className="mx-auto h-12 w-12 text-primary-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email</h2>
              <p className="text-gray-600 mb-6">Please wait while we verify your email address...</p>
              <LoadingSpinner size="lg" />
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <CheckCircleIcon className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
              <p className="text-gray-600 mb-6">
                Your email address has been successfully verified. You can now sign in to your account.
              </p>
              <Link
                to="/login"
                className="btn-primary inline-block px-6 py-3"
              >
                Go to Sign In
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <Alert type="error" message={errorMessage} />
              <div className="mt-6 space-y-3">
                <Link
                  to="/login"
                  className="btn-primary inline-block px-6 py-3 w-full text-center"
                >
                  Go to Sign In
                </Link>
                <Link
                  to="/register"
                  className="text-primary-600 hover:text-primary-500 text-sm block"
                >
                  Need a new account? Register here
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
